import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import {
    FuseNavigationItem,
    FuseNavigationService,
    FuseVerticalNavigationComponent
} from "../../../@fuse/components/navigation";
import { AuthService } from "../auth/auth.service";
import { OrganisationsService } from "../../modules/organisations/organisations.service";
import { Organisation } from 'app/modules/organisations/organisation.types';
import { NavConfig } from "../../modules/panel-control/controls-panel.types";
import { LocalStorageService } from '../localStorage/localStorage.service';
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { EntitycategoriesService } from "../../modules/entitycategories/entitycategories.service";
import { EntityCategory } from 'app/modules/entitycategories/entitycategories.types';
import { RdUser } from '../user/user.types';
import svgToMiniDataURI from 'mini-svg-data-uri';

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private _navigation: ReplaySubject<FuseNavigationItem[]> = new ReplaySubject<FuseNavigationItem[]>(1);
    organisation: Organisation;
    orgUser: RdUser;

    constructor(
        private _matIconRegistry: MatIconRegistry,
        private _domSanitizer: DomSanitizer,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _organisationsService: OrganisationsService,
        private _entityCategoriesService: EntitycategoriesService,
        private _localStorageService: LocalStorageService,
    ) {
        this.setNavigation();
        this._organisationsService.organisationName$
            .subscribe((organisationName: string) => {
                this.updateOrganisationName(organisationName);
            });
        this._organisationsService.organisation$
            .subscribe((organisation: Organisation) => {
                this.organisation = organisation;
                if (organisation?.config?.nav?.hub?.entities) {
                    this.setNavigation(this.getNavigationItemsFromNavConfig(organisation.config.nav.hub, true), this.getNavigationItemsFromNavConfig(organisation.config.nav.hub, false));
                } else {
                    this.setNavigation();
                }
            });

        this._entityCategoriesService.entitycategories$
            .subscribe((entityCategories: EntityCategory[]) => {
                const organisation = this._organisationsService.organisation;
                if (organisation) {
                    if (organisation?.config?.nav?.hub?.entities) {
                        this.setNavigation(this.getNavigationItemsFromNavConfig(organisation.config.nav.hub, true), this.getNavigationItemsFromNavConfig(organisation.config.nav.hub, false));
                    }
                }
            });
    }

    private getNavigationItemsFromNavConfig(navconfig: NavConfig, primary: boolean): FuseNavigationItem[] {

        if (!navconfig || !navconfig.entities || navconfig.entities.length === 0) {
            return [];
        }

        const itemsFromConfig = navconfig.entities.filter(navEntity => !navEntity.primary === !primary);
        if (itemsFromConfig.length === 0) {
            return [];
        }

        const items: FuseNavigationItem[] = []

        const entityCategories = this._entityCategoriesService.currentEntityCategories;
        itemsFromConfig.forEach(node => {
            // let icon = 'heroicons_outline:cube';
            let icon = 'hubicons:storage-box-icon';
            let svg = entityCategories?.find(entityCategory => entityCategory.id === node.entityCategoryId)?.icon;
            if (svg) {
                const optimizedSVGDataURI = svgToMiniDataURI(svg);
                this._matIconRegistry.addSvgIcon(node.entityCategoryId, this._domSanitizer.bypassSecurityTrustResourceUrl(optimizedSVGDataURI));
                icon = node.entityCategoryId;
            }

            items.push({
                id: `entities.${node.entityCategoryId}`,
                title: node.name,
                type: 'basic',
                icon: icon,
                link: `entities`,
                hidden: (item) => { return !this._authService.getIsAdmin() },
                queryParams: { rootCat: node.entityCategoryId }
            })
        });

        if (!primary) {
            const group: FuseNavigationItem = {
                id: 'assets',
                title: 'Assets',
                //subtitle: 'API Section',
                type: 'group',
                hidden: (item) => { return !this._authService.getIsAdmin() },
                children: items
            }

            return [group];
        }

        return items;
    }

    async setNavigation(dynamicPrimaryItems?: FuseNavigationItem[], dynamicSectionItems?: FuseNavigationItem[]) {
        this._navigation.next(this.getNavigationTree(dynamicPrimaryItems, dynamicSectionItems));
    }

    private getNavigationTree(dynamicPrimaryItems: FuseNavigationItem[] = [], dynamicSectionItems: FuseNavigationItem[] = []): FuseNavigationItem[] {
        const organisation = this._organisationsService.organisation;
        // The names for all of these are explicitly reset in language.component
        const userSection = [
            {
                id: 'main',
                title: this._localStorageService.localStorageGetItem('organisationName') ?? ' ',
                type: 'group',
                classes: {
                    title: ''
                },
                children: [
                    {
                        id: 'main.dashboard',
                        title: 'Dashboard',
                        type: 'basic',
                        icon: 'hubicons:item-list-icon',
                        link: '/dashboard'
                    },
                    {
                        id: 'main.submissions',
                        title: 'Submissions',
                        type: 'basic',
                        icon: 'hubicons:file-icon', // heroicons_outline:view-list
                        link: '/submissions',
                        // badge: {
                        //     title: 'New',
                        //     classes: 'px-2.5 bg-green-400 text-black rounded-full'
                        // }
                    },
                    {
                        id: 'main.jobs',
                        title: 'Jobs',
                        type: 'basic',
                        icon: 'hubicons:jobs-icon',
                        link: '/jobs',
                        hidden: (item) => { return !this._authService.getIsAdmin() || !this._organisationsService.organisation?.config?.usesJobSystem; }
                    },
                    {
                        id: 'main.recurring-jobs',
                        title: 'Recurring Jobs',
                        type: 'basic',
                        icon: 'hubicons:calendar-icon',
                        hidden: (item) => { return !this._authService.getIsAdmin() || !this._organisationsService.organisation?.config?.usesJobSystem; },
                        link: '/recurring-jobs'
                    },
                    ...dynamicPrimaryItems,
                    {
                        id: 'main.users',
                        title: 'Users',
                        type: 'basic',
                        icon: 'hubicons:people-icon',
                        link: '/users',
                        hidden: (item) => { return !this._authService.getIsAdmin(); }
                    },
                    {
                        id: 'main.contacts',
                        title: 'Contacts',
                        type: 'basic',
                        icon: 'hubicons:contacts-icon',
                        link: '/contacts',
                        hidden: (item) => { return !this._authService.getIsAdmin(); }
                    },
                    {
                        id: 'main.forms',
                        title: 'Forms',
                        type: 'basic',
                        icon: 'hubicons:forms-icon',
                        link: '/forms',
                        hidden: (item) => { return !this._authService.getIsSuperAdmin(); }
                    },
                    {
                        id: 'main.files',
                        title: 'Files',
                        type: 'collapsable',
                        icon: 'hubicons:file-explorer-icon',
                        hidden: (item) => { return !this._authService.getIsSuperAdmin(); },
                        children: [
                            {
                                id: 'main.files.images',
                                title: 'Images',
                                type: 'basic',
                                icon: 'hubicons:image-icon',
                                link: '/files/images',
                                hidden: (item) => { return !this._authService.getIsSuperAdmin(); }
                            },
                            {
                                id: 'main.files.documents',
                                title: 'Documents',
                                type: 'basic',
                                icon: 'hubicons:documents-icon',
                                link: '/files/documents',
                                hidden: (item) => { return !this._authService.getIsSuperAdmin(); }
                            },
                            {
                                id: 'main.files.maps',
                                title: 'Schematics',
                                type: 'basic',
                                icon: 'hubicons:map-icon',
                                link: '/files/schematics',
                                hidden: (item) => { return !this._authService.getIsSuperAdmin(); }
                            },
                        ]
                    },
                    {
                        id: 'main.resources',
                        title: 'Resources',
                        type: 'collapsable',
                        icon: 'hubicons:storage-box-icon',
                        hidden: (item) => { return !this._authService.getIsSuperAdmin(); },
                        children: [
                            {
                                id: 'main.resources.datasets',
                                title: 'Datasets',
                                type: 'basic',
                                icon: 'hubicons:menu-icon',
                                link: '/resources/datasets'
                            },
                            {
                                title: 'Headers',
                                type: 'basic',
                                icon: 'hubicons:headers-icon',
                                link: '/resources/headers'
                            },
                            {
                                id: 'main.resources.styles',
                                title: 'Styles',
                                type: 'basic',
                                icon: 'hubicons:swatch-icon',
                                link: '/resources/styles'
                            },
                            {
                                id: 'main.resources.distlists',
                                title: 'Distribution',
                                type: 'basic',
                                icon: 'hubicons:mail-icon',
                                link: '/resources/distribution'
                            },
                            {
                                id: 'main.resources.processes',
                                title: 'Processes',
                                type: 'basic',
                                icon: 'hubicons:more-icon',
                                link: '/resources/processes'
                            },
                            {
                                id: 'main.resources.workflows',
                                title: 'Workflows',
                                type: 'basic',
                                icon: 'hubicons:thunder-icon',
                                link: '/resources/workflows'
                            },
                            {
                                id: 'main.resources.report-definitions',
                                title: 'Report Definitions',
                                type: 'basic',
                                icon: 'heroicons_outline:document-text',
                                link: '/resources/report-definitions'
                            },
                        ]
                    },
                    {
                        id: 'main.organisation',
                        title: 'Organisation',
                        type: 'basic',
                        icon: 'hubicons:setting-icon',
                        link: '/organisation',
                        hidden: (item) => { return !this._authService.getIsSuperAdmin(); }
                    },
                    {
                        id: 'main.billing',
                        title: 'Billing',
                        type: 'basic',
                        icon: 'hubicons:billing-icon',
                        link: '/billing',
                        hidden: (item) => { return this.checkBilling() }
                    }
                ]
            }
        ];



        const panelControlSection = [
            {
                id: 'controlpanel',
                title: 'Control Panel',
                //subtitle: 'API Section',
                type: 'group',
                hidden: (item) => { return !this._authService.getIsSuperAdmin() },
                children: [
                    {
                        id: 'panelView',
                        title: 'Control Panel',
                        type: 'basic',
                        icon: 'hubicons:config-icon', // heroicons_outline:view-list
                        link: '/control-panel',
                    }
                ]
            }
        ];



        const entitiesSection = [
            {
                id: 'entities',
                title: 'Entities',
                //subtitle: 'API Section',
                type: 'group',
                hidden: (item) => { return !this._authService.getIsSuperAdmin() },
                children: [
                    {
                        id: 'main.entitycategories',
                        title: 'Entity Categories',
                        type: 'basic',
                        icon: 'hubicons:entity-categories-icon',
                        link: '/entitycategories',
                    },
                    {
                        id: 'main.entitytypes',
                        title: 'Entity Types',
                        type: 'basic',
                        icon: 'hubicons:entity-types-icon',
                        link: '/entitytypes',
                    },
                    {
                        id: 'main.entities',
                        title: 'Entities',
                        type: 'basic',
                        icon: 'hubicons:entities-icon',
                        link: '/entities',
                    },
                    {
                        id: 'main.entityconditionrules',
                        title: 'Entity Condition Rules',
                        type: 'basic',
                        icon: 'hubicons:warning-icon',
                        link: '/entityconditionrules',
                    },
                ]
            }
        ];

        const dynamicSection = [].concat(dynamicSectionItems);

        const rubbishBinSection = [
            {
                id: 'bin',
                title: 'Bin',
                type: 'group',
                hidden: (item) => { return !this._authService.getIsAdmin() },
                children: [
                    {
                        id: 'main.binEntities',
                        title: 'Assets',
                        type: 'basic',
                        icon: 'hubicons:entities-icon',
                        link: '/bin/entities'
                    }
                ]
            }
        ]


        const apiSection = [
            {
                id: 'api',
                title: 'API',
                //subtitle: 'API Section',
                type: 'group',
                hidden: (item) => { return !this._authService.getHasApiAccess() },
                children: [
                    {
                        id: 'api.api',
                        title: 'API',
                        type: 'basic',
                        icon: 'hubicons:world-globe-icon',
                        link: '/api'
                    }
                ]
            }
        ];

        const superSection = [
            {
                id: 'superDivider',
                type: 'divider'
            },
            {
                id: 'admin',
                title: 'Admin',
                type: 'group',
                hidden: (item) => { return !this._authService.getIsSuperAdmin() },
                children: [
                    {
                        id: 'admin.organisations',
                        title: 'Switch Organisation',
                        type: 'basic',
                        icon: 'hubicons:house-icon',
                        link: '/organisations'
                    },
                    {
                        id: 'admin.userSearch',
                        title: 'User Search',
                        type: 'basic',
                        icon: 'hubicons:search-find-icon',
                        link: '/userSearch',
                    },
                    {
                        id: 'admin.pendingTasks',
                        title: 'Pending Tasks',
                        type: 'collapsable',
                        icon: 'hubicons:storage-box-icon',
                        children: [
                            {
                                id: 'admin.pendingTasks.tasks',
                                title: 'Tasks',
                                type: 'basic',
                                icon: 'hubicons:menu-icon',
                                link: '/tasks'
                            },
                            {
                                id: 'admin.pendingTasks.stuckTasks',
                                title: 'Submitted',
                                type: 'basic',
                                icon: 'hubicons:file-icon',
                                link: '/stucktasks'
                            }
                        ]
                    },
                    {
                        id: 'admin.formsEditor',
                        title: 'Form Editor',
                        type: 'basic',
                        icon: 'hubicons:forms-icon',
                        link: '/forms-editor'
                    },
                    {
                        id: 'admin.formBuilder',
                        title: 'Form Uploader',
                        type: 'basic',
                        icon: 'hubicons:forms-icon',
                        link: '/form-builder',
                    },
                ]
            }
        ]

        return [].concat(userSection, entitiesSection, dynamicSection, rubbishBinSection, apiSection, panelControlSection, superSection);
    }

    get navigation$(): Observable<FuseNavigationItem[]> {
        return this._navigation.asObservable();
    }

    private updateOrganisationName(organisationName: string) {
        // Get the component -> navigation data -> item
        const navComponent = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;

        if (!navigation) return;

        // Update the Main Section title with the Organisation Name
        const mainSectionItem = this._fuseNavigationService.getItem('main', navigation);
        if (mainSectionItem) {
            mainSectionItem.title = organisationName;
            navComponent.refresh();
        }
    }

    checkBilling(): boolean {
        if ((this._authService.getIsSuperAdmin() && this.organisation?.billable) || (this._authService.getIsOrgOwner() && this.organisation?.billable)) return false
        else return true
    }
}
